import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import Logo from "../../components/Logo";
import { LogIn, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const { login, user, checking } = useAdminAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!checking && user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.username, form.password);
      navigate("/admin");
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0c1626]">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 brand-gradient">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-[#0c1626] flex items-center justify-center">
            <Logo size={30} />
          </span>
          <span className="leading-none">
            <span className="block font-display text-xl text-white">Novocreste</span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-white/70">Admin Console</span>
          </span>
        </div>
        <div>
          <h1 className="font-display text-4xl text-white leading-tight">
            Manage your global pharmaceutical presence
          </h1>
          <p className="mt-4 text-white/80 max-w-md">
            Update products, content, certifications and view every RFQ and enquiry from one secure dashboard.
          </p>
          <div className="mt-8 flex items-center gap-2 text-white/70 text-sm">
            <ShieldCheck className="h-4 w-4" /> Secure JWT-protected access
          </div>
        </div>
        <p className="text-white/40 text-xs">© {new Date().getFullYear()} Novocreste International Pvt Ltd</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <span className="h-11 w-11 rounded-xl bg-[#0c1626] flex items-center justify-center">
              <Logo size={30} />
            </span>
            <span className="font-display text-xl text-white">Novocreste Admin</span>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <h2 className="font-display text-2xl text-[#0f1e33]">Welcome back</h2>
            <p className="text-sm text-[#5a6b7e] mt-1">Sign in to the admin console.</p>

            {error && (
              <div className="mt-4 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm px-3 py-2">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="username">Username</Label>
                <Input id="username" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} placeholder="admin" required className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <PasswordInput id="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••••" required className="h-11" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#0e7490] hover:bg-[#0b5563] text-white h-11">
                <LogIn className="h-4 w-4 mr-2" /> {loading ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
